/**
 * 학습 진도 관리 localStorage 유틸리티
 * 한 가지 자격증에 장기간 집중하는 패턴에 최적화
 */

// 기본 학습 진도 구조
const createDefaultProgress = (userId, certification = '정보처리기사') => ({
  userId,
  currentCertification: certification,
  overallProgress: 0,
  examProgress: {},
  subjectProgress: {},
  studyPattern: {
    totalStudyDays: 0,
    consecutiveStudyDays: 0,
    currentStreak: 0,
    lastStudyDate: null,
    averageSessionTime: 0,
    totalSessionTime: 0,
    sessionCount: 0,
    preferredStudyTime: null,
    weeklyStudyFrequency: 0,
    peakPerformanceTime: null,
    studyHistory: [] // { date: 'YYYY-MM-DD', sessionTime: minutes, questionsAnswered: number }
  },
  goals: {
    targetExamDate: null,
    targetScore: 90,
    dailyQuestionGoal: 50,
    weeklyStudyHours: 20
  },
  statistics: {
    totalQuestionsSolved: 0,
    totalCorrectAnswers: 0,
    overallAccuracy: 0,
    averageQuestionTime: 0,
    bestStreak: 0,
    createdAt: new Date().toISOString(),
    lastUpdated: new Date().toISOString()
  }
});

// 학습 진도 데이터 가져오기
export const getLearningProgress = (userId) => {
  if (!userId) return null;
  
  try {
    const storageKey = `learningProgress_${userId}`;
    const stored = localStorage.getItem(storageKey);
    
    if (!stored) {
      const defaultProgress = createDefaultProgress(userId);
      saveLearningProgress(userId, defaultProgress);
      return defaultProgress;
    }
    
    const progress = JSON.parse(stored);
    
    // 데이터 무결성 검사 및 업데이트
    const updatedProgress = validateAndUpdateProgress(progress);
    
    if (JSON.stringify(progress) !== JSON.stringify(updatedProgress)) {
      saveLearningProgress(userId, updatedProgress);
    }
    
    return updatedProgress;
  } catch (error) {
    console.error('학습 진도 데이터 로딩 실패:', error);
    return createDefaultProgress(userId);
  }
};

// 학습 진도 데이터 저장
export const saveLearningProgress = (userId, progressData) => {
  if (!userId || !progressData) return false;
  
  try {
    const storageKey = `learningProgress_${userId}`;
    const updatedData = {
      ...progressData,
      lastUpdated: new Date().toISOString()
    };
    
    localStorage.setItem(storageKey, JSON.stringify(updatedData));
    return true;
  } catch (error) {
    console.error('학습 진도 데이터 저장 실패:', error);
    return false;
  }
};

// 문제 풀이 완료 시 진도 업데이트
export const updateQuestionProgress = (userId, questionData) => {
  if (!userId || !questionData) return;
  
  const progress = getLearningProgress(userId);
  if (!progress) return;
  
  const { examType, subject, isCorrect, timeSpent, sessionId, totalQuestions, questionNumber } = questionData;
  const today = new Date().toISOString().split('T')[0];
  
  // 진단평가가 아닌 경우에만 전체 통계 업데이트
  const isDiagnosticQuestion = examType && examType.startsWith('diagnostic_');
  
  if (!isDiagnosticQuestion) {
    // 전체 통계 업데이트
    progress.statistics.totalQuestionsSolved += 1;
    if (isCorrect) {
      progress.statistics.totalCorrectAnswers += 1;
    }
    progress.statistics.overallAccuracy = 
      (progress.statistics.totalCorrectAnswers / progress.statistics.totalQuestionsSolved) * 100;
    
    // 평균 문제 풀이 시간 업데이트
    const currentAvgTime = progress.statistics.averageQuestionTime || 0;
    const totalSolvedQuestions = progress.statistics.totalQuestionsSolved;
    progress.statistics.averageQuestionTime = 
      ((currentAvgTime * (totalSolvedQuestions - 1)) + (timeSpent || 0)) / totalSolvedQuestions;
  }
  
  // 시험별 진도 업데이트
  if (examType) {
    const examKey = examType;
    if (!progress.examProgress[examKey]) {
      // 진단평가와 일반 시험 구분하여 초기값 설정
      const isDiagnostic = examKey.startsWith('diagnostic_');
      const defaultTotalQuestions = isDiagnostic ? 20 : 100; // 진단평가는 보통 20문제, 일반 시험은 100문제
      const actualTotalQuestions = totalQuestions || defaultTotalQuestions; // 전달받은 값 우선 사용
      
      progress.examProgress[examKey] = {
        completed: false,
        progress: 0,
        questionsAnswered: 0,
        correctAnswers: 0,
        totalTime: 0,
        totalQuestions: actualTotalQuestions,
        lastStudied: today,
        isDiagnostic: isDiagnostic,
        solvedQuestions: [] // 실제로 푼 문제 번호들 추적
      };
    }
    
    const examProgress = progress.examProgress[examKey];
    
    // 기존 솔브드 퀘스천 배열이 없으면 초기화
    if (!examProgress.solvedQuestions) {
      examProgress.solvedQuestions = [];
    }
    
    // 문제 번호가 있고, 아직 기록되지 않은 경우에만 추가
    if (questionNumber && !examProgress.solvedQuestions.includes(questionNumber)) {
      examProgress.solvedQuestions.push(questionNumber);
      examProgress.solvedQuestions.sort((a, b) => a - b); // 번호순 정렬
    }
    
    examProgress.questionsAnswered += 1;
    if (isCorrect) examProgress.correctAnswers += 1;
    examProgress.totalTime += timeSpent || 0;
    examProgress.lastStudied = today;
    examProgress.accuracy = (examProgress.correctAnswers / examProgress.questionsAnswered) * 100;
    
    // 진도율 계산 (총 문제 수 대비)
    examProgress.progress = Math.round((examProgress.questionsAnswered / examProgress.totalQuestions) * 100);
  }
  
  // 과목별 진도 업데이트 (진단평가가 아닌 경우에만)
  if (subject && !isDiagnosticQuestion) {
    if (!progress.subjectProgress[subject]) {
      progress.subjectProgress[subject] = {
        totalQuestions: 1000, // 예상 총 문제 수 (실제로는 동적으로 계산)
        solvedQuestions: 0,
        correctAnswers: 0,
        accuracy: 0,
        averageTime: 0,
        totalTime: 0,
        lastStudied: today
      };
    }
    
    const subjectProgress = progress.subjectProgress[subject];
    subjectProgress.solvedQuestions += 1;
    if (isCorrect) subjectProgress.correctAnswers += 1;
    subjectProgress.totalTime += timeSpent || 0;
    subjectProgress.lastStudied = today;
    subjectProgress.accuracy = (subjectProgress.correctAnswers / subjectProgress.solvedQuestions) * 100;
    subjectProgress.averageTime = subjectProgress.totalTime / subjectProgress.solvedQuestions;
  }
  
  // 학습 패턴 업데이트 (진단평가가 아닌 경우에만)
  if (!isDiagnosticQuestion) {
    updateStudyPattern(progress, timeSpent || 0, today);
  }
  
  // 전체 진도율 계산
  progress.overallProgress = calculateOverallProgress(progress);
  
  saveLearningProgress(userId, progress);
};

// 학습 세션 완료 시 호출
export const updateSessionComplete = (userId, sessionData) => {
  if (!userId || !sessionData) return;
  
  const progress = getLearningProgress(userId);
  if (!progress) return;
  
  const { sessionTime, questionsAnswered, correctAnswers, examType, completedExam } = sessionData;
  const today = new Date().toISOString().split('T')[0];
  
  // 세션 데이터 업데이트
  progress.studyPattern.sessionCount += 1;
  progress.studyPattern.totalSessionTime += sessionTime;
  progress.studyPattern.averageSessionTime = 
    progress.studyPattern.totalSessionTime / progress.studyPattern.sessionCount;
  
  // 학습 기록 추가
  progress.studyPattern.studyHistory.push({
    date: today,
    sessionTime,
    questionsAnswered,
    correctAnswers,
    accuracy: questionsAnswered > 0 ? (correctAnswers / questionsAnswered) * 100 : 0,
    examType
  });
  
  // 최근 30일 기록만 유지
  progress.studyPattern.studyHistory = progress.studyPattern.studyHistory
    .slice(-30)
    .sort((a, b) => new Date(b.date) - new Date(a.date));
  
  // 시험 완료 처리
  if (completedExam && examType) {
    const examKey = examType;
    if (progress.examProgress[examKey]) {
      progress.examProgress[examKey].completed = true;
      progress.examProgress[examKey].score = Math.round((correctAnswers / questionsAnswered) * 100);
      progress.examProgress[examKey].completedAt = today;
    }
  }
  
  saveLearningProgress(userId, progress);
};

// 학습 패턴 분석 및 업데이트
const updateStudyPattern = (progress, sessionTime, today) => {
  const pattern = progress.studyPattern;
  
  // 연속 학습일 계산
  const lastStudyDate = pattern.lastStudyDate;
  if (lastStudyDate) {
    const daysDiff = Math.floor((new Date(today) - new Date(lastStudyDate)) / (1000 * 60 * 60 * 24));
    
    if (daysDiff === 1) {
      // 연속 학습
      pattern.currentStreak += 1;
      pattern.consecutiveStudyDays = pattern.currentStreak;
    } else if (daysDiff === 0) {
      // 같은 날 추가 학습
      // 연속일 유지
    } else {
      // 연속 끊어짐
      pattern.currentStreak = 1;
      pattern.consecutiveStudyDays = Math.max(pattern.consecutiveStudyDays, pattern.currentStreak);
    }
  } else {
    pattern.currentStreak = 1;
    pattern.consecutiveStudyDays = 1;
  }
  
  // 최고 연속 학습일 기록 업데이트
  progress.statistics.bestStreak = Math.max(
    progress.statistics.bestStreak || 0, 
    pattern.currentStreak
  );
  
  pattern.lastStudyDate = today;
  
  // 총 학습일 계산
  const uniqueDates = [...new Set([
    ...pattern.studyHistory.map(h => h.date),
    today
  ])];
  pattern.totalStudyDays = uniqueDates.length;
  
  // 선호 학습 시간대 분석
  const currentHour = new Date().getHours();
  pattern.preferredStudyTime = getTimeOfDay(currentHour);
};

// 시간대 분류
const getTimeOfDay = (hour) => {
  if (hour >= 6 && hour < 12) return '오전';
  if (hour >= 12 && hour < 18) return '오후';
  if (hour >= 18 && hour < 24) return '저녁';
  return '새벽';
};

// 전체 진도율 계산 (진단평가 제외)
const calculateOverallProgress = (progress) => {
  // 진단평가를 제외한 실제 시험만 계산
  const regularExams = Object.values(progress.examProgress).filter(exam => !exam.isDiagnostic);
  const completedExams = regularExams.filter(exam => exam.completed).length;
  const totalExams = Math.max(regularExams.length, 10); // 최소 10개 시험 기준
  
  const subjectProgress = Object.values(progress.subjectProgress);
  const avgSubjectProgress = subjectProgress.length > 0 
    ? subjectProgress.reduce((acc, subject) => 
        acc + (subject.solvedQuestions / subject.totalQuestions) * 100, 0
      ) / subjectProgress.length 
    : 0;
  
  // 시험 완료율 60% + 과목별 진도율 40%
  return Math.round((completedExams / totalExams) * 60 + avgSubjectProgress * 0.4);
};

// 데이터 무결성 검사 및 업데이트
const validateAndUpdateProgress = (progress) => {
  // 필수 필드 검사 및 기본값 설정
  if (!progress.studyPattern) {
    progress.studyPattern = createDefaultProgress(progress.userId).studyPattern;
  }
  
  if (!progress.statistics) {
    progress.statistics = createDefaultProgress(progress.userId).statistics;
    progress.statistics.createdAt = progress.createdAt || new Date().toISOString();
  }
  
  if (!progress.goals) {
    progress.goals = createDefaultProgress(progress.userId).goals;
  }
  
  // 정확도 재계산
  const totalQuestions = progress.statistics.totalQuestionsSolved || 0;
  const correctAnswers = progress.statistics.totalCorrectAnswers || 0;
  
  if (totalQuestions > 0) {
    progress.statistics.overallAccuracy = (correctAnswers / totalQuestions) * 100;
  }
  
  return progress;
};

// 학습 목표 설정
export const updateLearningGoals = (userId, goals) => {
  if (!userId || !goals) return false;
  
  const progress = getLearningProgress(userId);
  if (!progress) return false;
  
  progress.goals = {
    ...progress.goals,
    ...goals
  };
  
  return saveLearningProgress(userId, progress);
};

// 학습 통계 조회
export const getLearningStats = (userId, period = 'week') => {
  const progress = getLearningProgress(userId);
  if (!progress) return null;
  
  const now = new Date();
  const periodStart = new Date();
  
  switch (period) {
    case 'week':
      periodStart.setDate(now.getDate() - 7);
      break;
    case 'month':
      periodStart.setMonth(now.getMonth() - 1);
      break;
    case 'year':
      periodStart.setFullYear(now.getFullYear() - 1);
      break;
    default:
      periodStart.setDate(now.getDate() - 7);
  }
  
  const periodHistory = progress.studyPattern.studyHistory.filter(
    h => new Date(h.date) >= periodStart
  );
  
  const totalQuestions = periodHistory.reduce((acc, h) => acc + h.questionsAnswered, 0);
  const totalCorrect = periodHistory.reduce((acc, h) => acc + h.correctAnswers, 0);
  const totalTime = periodHistory.reduce((acc, h) => acc + h.sessionTime, 0);
  
  return {
    period,
    studyDays: periodHistory.length,
    totalQuestions,
    totalCorrect,
    accuracy: totalQuestions > 0 ? (totalCorrect / totalQuestions) * 100 : 0,
    totalTime,
    averageSessionTime: periodHistory.length > 0 ? totalTime / periodHistory.length : 0,
    dailyAverage: {
      questions: totalQuestions / 7, // 주간 기준
      time: totalTime / 7
    }
  };
};

// 진도 데이터 내보내기
export const exportProgressData = (userId) => {
  const progress = getLearningProgress(userId);
  if (!progress) return null;
  
  return {
    exportDate: new Date().toISOString(),
    userId: progress.userId,
    certification: progress.currentCertification,
    data: progress
  };
};

// 진도 데이터 초기화
export const resetLearningProgress = (userId, certification) => {
  if (!userId) return false;
  
  try {
    const storageKey = `learningProgress_${userId}`;
    const newProgress = createDefaultProgress(userId, certification);
    localStorage.setItem(storageKey, JSON.stringify(newProgress));
    return true;
  } catch (error) {
    console.error('학습 진도 데이터 초기화 실패:', error);
    return false;
  }
};